import "./MakeBooking.css";
import { useEffect, useMemo, useState } from "react";
import { PulseLoader } from "react-spinners";
import { Trans, useTranslation } from "react-i18next";
import { verifyAppointmentCheckoutSession } from "./Requests";
import {DateTime } from "luxon"
import { formatMinor } from "../Components/Reusable/CurrencyFormatter/CurrencyFormatter";

function getQueryParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

const BookingPaymentResponse = () => {
  const { t, i18n } = useTranslation("makeBooking");

  const [sessionId] = useState(() => getQueryParam("session_id") || "");
  const [statusParam] = useState(() => getQueryParam("status") || "");
  const [shopParam] = useState(() => getQueryParam("shopId") || "");
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [config, setConfig] = useState(null);
  const [appointment, setAppointment] = useState(null);

  const runVerify = async () => {
    if (statusParam === "cancelled") {
      setVerified(false);
      setLoading(false);
      return;
    }

    if (!sessionId) {
      setVerified(false);
      setLoading(false);
      return;
    }

    try {
      const res = await verifyAppointmentCheckoutSession(sessionId);
      const data = res?.data;

      if (data?.isValid) {
        setConfig(data.config || null);
        setAppointment(data.calendarAppointment || null);
        setVerified(true);

        if (data?.config?.bookingPortalLanguage) {
          i18n.changeLanguage(data.config.bookingPortalLanguage);
        }

        if (data?.config?.shopName) {
          document.title = data.config.shopName;
        }

        return;
      }

      setVerified(false);
    } catch (e) {
      setVerified(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, statusParam]);

  const hasNotes = !!config?.appointmentReminderNotesToCustomer?.trim();
  const hasCancellation = !!config?.appointmentCancellationPolicyToCustomer?.trim();

  const hasAddress = [
    config?.addressNumberName,
    config?.addressFirstLine,
    config?.addressSecondLine,
    config?.addressCity,
    config?.addressCounty,
    config?.addressPostcode,
  ].some(Boolean);

  const contactEmail = config?.emailForCustomer || null;
  const contactPhone = config?.phoneNumberForCustomer || null;
  const website = config?.websiteLink || null;
  const barberShopId = config?.barberShopId || appointment?.shopId || null;

  const selectedDate = appointment?.startDateTime ? new Date(DateTime.fromISO(appointment.startDateTime, {zone: "utc"}).setZone(config.timeZone).toString().split("+")[0]) : null;
  const selectedTime = useMemo(() => {
    if (!appointment?.startDateTime) return "";
    const dd = new Date(appointment.startDateTime);
    const d = new Date(DateTime.fromISO(appointment.startDateTime, {zone: "utc"}).setZone(config.timeZone).toString().split("+")[0])
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  }, [appointment?.startDateTime]);

  const mapUrl = useMemo(() => {
    if (!config?.addressPostcode) return null;

    const query = [
      config?.addressNumberName,
      config?.addressFirstLine,
      config?.addressSecondLine,
      config?.addressPostcode,
    ]
      .filter(Boolean)
      .join(" ");

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }, [
    config?.addressNumberName,
    config?.addressFirstLine,
    config?.addressSecondLine,
    config?.addressPostcode,
  ]);

  if (loading) {
    return (
      <div
        data-theme={config?.themeId === 1 ? "one" : config?.themeId === 2 ? "two" : "one"}
        className="booking-portal-background"
        style={{ minHeight: "100vh" }}
      >
        <div style={{ height: "100vh" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ marginTop: "2rem" }}>
              <PulseLoader color={"#333"} size={25} aria-label="Loading" data-testid="loader" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!verified || !config || !appointment) {
    return (
      <div
        data-theme={config?.themeId === 1 ? "one" : config?.themeId === 2 ? "two" : "one"}
        className="booking-portal-background"
        style={{ minHeight: "100vh" }}
      >
        <div className="cancel-appointment-container">
          <div className="cancel-appointment-card" style={{ marginTop: "2rem", marginBottom: "2rem" }}>
            <div>
              <div className="icon-problem-wrapper">
                <span className="problem-icon" aria-hidden="true">&#x2718;</span>
              </div>
              <h1>{t("appointmentFailed.titles.heading")}</h1>
              <p>{t("appointmentFailed.messages.body")}</p>
              <div className="confirm__actions">
                <button
                  type="button"
                  className="select-btn mt-3"
                  style={{ marginLeft: "auto", marginRight: "auto" }}
                    onClick={() => window.location.href = `make-a-booking?id=${shopParam}`}
                >
                  {t("appointmentFailed.actions.tryAgain")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-theme={config.themeId === 1 ? "one" : config.themeId === 2 ? "two" : "one"}
      className="booking-portal-background"
      style={{ minHeight: "100vh" }}
    >
      <div className="cancel-appointment-container p-2">
        <div className="confirm__card">
          <div className="confirm__header">
            <div className="confirm__titles">
              <h1 className="confirm__title">
                {t("appointmentConfirmation.titles.booked")}
              </h1>
              <p className="confirm__subtitle">
                <Trans
                  i18nKey="appointmentConfirmation.subtitles.confirmationSent"
                  ns="makeBooking"
                  values={{ email: appointment.email }}
                  components={{ b: <b /> }}
                />
              </p>
            </div>

            <div className="confirm__icon" aria-hidden="true">
              <span className="check-icon">&#10003;</span>
            </div>
          </div>

          <div className="summaryTiles">
            <h3 className="summaryTitle">
              {t("appointmentConfirmation.titles.summary")}
            </h3>

            <ul className="tiles">
              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.service")}
                </span>
                <span className="tileValue tileClamp">{appointment.serviceName}</span>
              </li>

              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.with")}
                </span>
                <span className="tileValue tileClamp">{appointment.staffName}</span>
              </li>

              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.date")}
                </span>
                <span className="tileValue tileNowrap">
                  {selectedDate
                    ? new Intl.DateTimeFormat(i18n.language || "en", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                        .format(selectedDate)
                        .replace(/,/g, "")
                    : ""}
                </span>
              </li>

              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.time")}
                </span>
                <span className="tileValue tileMono">{selectedTime}</span>
              </li>
              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.paidOnline")}
                </span>
                <span className="tileValue tileMono">{formatMinor(appointment.paidAmount, config.currency)}</span>
              </li>
              <li className="tile">
                <span className="tileLabel">
                  {t("appointmentConfirmation.labels.payInShop")}
                </span>
                <span className="tileValue tileMono">{formatMinor(appointment.servicePriceAmount-appointment.paidAmount, config.currency)}</span>
              </li>
            </ul>
          </div>

          {hasNotes && (
            <div className="confirm__section">
              <h4 className="confirm__h4">
                {t("appointmentConfirmation.titles.notes")}
              </h4>
              <p className="confirm__text">{config.appointmentReminderNotesToCustomer}</p>
            </div>
          )}

          {hasCancellation && (
            <div className="confirm__section">
              <h4 className="confirm__h4">
                {t("appointmentConfirmation.titles.cancellationPolicy")}
              </h4>
              <p className="confirm__text">{config.appointmentCancellationPolicyToCustomer}</p>
            </div>
          )}

          {(hasAddress || website || contactEmail || contactPhone) && (
            <div className="confirm__grid">
              {hasAddress && (
                <div className="confirm__panel">
                  <h4 className="confirm__h4">
                    {t("appointmentConfirmation.titles.location")}
                  </h4>
                  <address className="confirm__address">
                    {config.addressNumberName && <span>{config.addressNumberName}, </span>}
                    {config.addressFirstLine && <span>{config.addressFirstLine}, </span>}
                    {config.addressSecondLine && <span>{config.addressSecondLine}, </span>}
                    {config.addressCity && <span>{config.addressCity}, </span>}
                    {config.addressCounty && <span>{config.addressCounty}, </span>}
                    {config.addressPostcode && <span>{config.addressPostcode}</span>}
                  </address>

                  {mapUrl && (
                    <a
                      className="confirm__link"
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("appointmentConfirmation.actions.getDirections")}
                    </a>
                  )}
                </div>
              )}

              {(website || contactEmail || contactPhone) && (
                <div className="confirm__panel">
                  <h4 className="confirm__h4">
                    {t("appointmentConfirmation.titles.contact")}
                  </h4>

                  {website && !/^https?:\/\/$/i.test(website.trim()) && (
                    <p className="confirm__text">
                      <a
                        className="confirm__link"
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {website}
                      </a>
                    </p>
                  )}

                  {contactEmail && (
                    <p className="confirm__text">
                      <a className="confirm__link" href={`mailto:${contactEmail}`}>
                        {contactEmail}
                      </a>
                    </p>
                  )}

                  {contactPhone && (
                    <p className="confirm__text">
                      <a className="confirm__link" href={`tel:${contactPhone}`}>
                        {contactPhone}
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPaymentResponse;